import api from './config';
import type { DiscoverComment, MyRecommendationsResponse, Recommendation } from '@/types';
import { trackEvent } from '@/utils/analytics';

export const createRecommendation = async (recommendation: Recommendation) => {
  const response = await api.post('/recommendation', recommendation);
  const audience = recommendation.parentRecommendationId
    ? 're-reco'
    : recommendation.groupIds?.length
      ? 'circles'
      : 'public';
  trackEvent('Reco', 'share', audience);
  return response.data;
};

/**
 * A page of the current user's own recommendations with their impact (reach,
 * reactions, streaming-app openings) — powers the "Impact de mes
 * recommandations" screen. Supports pagination and a title/artist search.
 */
export const getMyRecommendations = async (
  page = 1,
  q = '',
  pageSize = 10,
): Promise<MyRecommendationsResponse> => {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (q.trim()) params.set('q', q.trim());
  const response = await api.get<MyRecommendationsResponse>(`/recommendation/mine?${params}`);
  return response.data;
};

/** Report a recommendation's comment for moderation (offensive content). */
export const moderateRecommendation = async (commentId: string, reason = 'default') => {
  const response = await api.post('/moderation-request/create', { commentId, reason });
  return response.data;
};

/** Delete a recommendation (creator only — enforced by backend permissions). */
export const deleteRecommendation = async (documentId: string) => {
  const response = await api.delete(`/recommendations/${documentId}`);
  return response.data;
};

/**
 * caption-edit-retranslate — rewrite the author's legend (sender only, checked
 * server side). The server resets `translations` to null in the same write, so
 * the returned legend is what every reader sees until the new translation lands.
 */
export const updateCaption = async (recoId: number, content: string): Promise<DiscoverComment> => {
  const response = await api.put(`/recommendation/${recoId}/caption`, { content });
  return response.data.data;
};
